import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Button, Chip, CircularProgress, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select,
  FormControl, InputLabel, IconButton, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';

// ── Table registry ────────────────────────────────────────────────────────────
const TABLES = [
  {
    id: 'customer-orders', label: 'Замовлення клієнтів', endpoint: '/customer-orders/',
    columns: [
      { field: 'number', header: '№', editable: false },
      { field: 'customer_name', header: 'Клієнт', editable: false },
      { field: 'order_date', header: 'Дата', editable: false, format: (v) => v ? new Date(v).toLocaleDateString('uk-UA') : '—' },
      { field: 'deadline', header: 'Дедлайн', editable: true, type: 'date' },
      { field: 'status', header: 'Статус', editable: true, type: 'select', options: ['new','in_progress','done','cancelled'] },
      { field: 'comment', header: 'Коментар', editable: true, type: 'text' },
    ],
  },
  {
    id: 'products', label: 'Вироби', endpoint: '/products/',
    columns: [
      { field: 'article', header: 'Артикул', editable: true, type: 'text' },
      { field: 'name', header: 'Назва', editable: true, type: 'text' },
      {
        field: 'product_weight',
        header: 'Вага (г)',
        editable: true,
        type: 'number',
        /** у БД кг; UI редагує грами */
        uiUnit: 'g',
        format: (v) => (v == null || v === '' ? '—' : `${Math.round(Number(v) * 1000)}`),
      },
      { field: 'cycle_time_seconds', header: 'Цикл (сек)', editable: true, type: 'number' },
      { field: 'is_active', header: 'Активний', editable: true, type: 'boolean' },
    ],
  },
  {
    id: 'raw-materials', label: 'Сировина', endpoint: '/raw-materials/',
    columns: [
      { field: 'article', header: 'Артикул', editable: true, type: 'text' },
      { field: 'name', header: 'Назва', editable: true, type: 'text' },
      { field: 'unit', header: 'Одиниця', editable: true, type: 'text' },
      { field: 'min_stock', header: 'Мін. залишок', editable: true, type: 'number' },
    ],
  },
  {
    id: 'dyes', label: 'Барвники', endpoint: '/dyes/',
    columns: [
      { field: 'article', header: 'Артикул', editable: true, type: 'text' },
      { field: 'name', header: 'Назва', editable: true, type: 'text' },
      { field: 'unit', header: 'Одиниця', editable: true, type: 'text' },
    ],
  },
  {
    id: 'packaging-materials', label: 'Упаковка', endpoint: '/packaging-materials/',
    columns: [
      { field: 'article', header: 'Артикул', editable: true, type: 'text' },
      { field: 'name', header: 'Назва', editable: true, type: 'text' },
      { field: 'unit', header: 'Одиниця', editable: true, type: 'text' },
    ],
  },
  {
    id: 'consumable-materials', label: 'Розхідні матеріали', endpoint: '/consumable-materials/',
    columns: [
      { field: 'article', header: 'Артикул', editable: true, type: 'text' },
      { field: 'name', header: 'Назва', editable: true, type: 'text' },
      { field: 'unit', header: 'Одиниця', editable: true, type: 'text' },
    ],
  },
  {
    id: 'customers', label: 'Клієнти', endpoint: '/customers/',
    columns: [
      { field: 'name', header: 'Назва', editable: true, type: 'text' },
      { field: 'phone', header: 'Телефон', editable: true, type: 'text' },
      { field: 'email', header: 'Email', editable: true, type: 'text' },
      { field: 'contact_person', header: 'Контакт', editable: true, type: 'text' },
    ],
  },
  {
    id: 'suppliers', label: 'Постачальники', endpoint: '/suppliers/',
    columns: [
      { field: 'name', header: 'Назва', editable: true, type: 'text' },
      { field: 'phone', header: 'Телефон', editable: true, type: 'text' },
      { field: 'email', header: 'Email', editable: true, type: 'text' },
    ],
  },
  {
    id: 'production-orders', label: 'Виробничі замовлення', endpoint: '/production-orders/',
    columns: [
      { field: 'id', header: 'ID', editable: false },
      { field: 'product_name', header: 'Виріб', editable: false },
      { field: 'quantity', header: 'Кількість', editable: true, type: 'number' },
      { field: 'status', header: 'Статус', editable: false },
      { field: 'planned_start', header: 'Початок', editable: true, type: 'datetime-local', format: (v) => v ? new Date(v).toLocaleString('uk-UA') : '—' },
    ],
  },
  {
    id: 'purchase-orders', label: 'Закупівлі', endpoint: '/purchase-orders/',
    columns: [
      { field: 'id', header: 'ID', editable: false },
      { field: 'supplier_name', header: 'Постачальник', editable: false },
      { field: 'status', header: 'Статус', editable: true, type: 'select', options: ['draft','ordered','received','cancelled'] },
      { field: 'total_amount', header: 'Сума', editable: true, type: 'number' },
      { field: 'order_date', header: 'Дата', editable: false, format: (v) => v ? new Date(v).toLocaleDateString('uk-UA') : '—' },
    ],
  },
  {
    id: 'stock-items', label: 'Залишки складу', endpoint: '/stock-items/',
    columns: [
      { field: 'id', header: 'ID', editable: false },
      { field: 'raw_material_name', header: 'Сировина', editable: false },
      { field: 'dye_name', header: 'Барвник', editable: false },
      { field: 'packaging_material_name', header: 'Упаковка', editable: false },
      { field: 'quantity', header: 'Кількість', editable: true, type: 'number' },
      { field: 'unit', header: 'Одиниця', editable: false },
    ],
  },
  {
    id: 'product-kits', label: 'Комплекти', endpoint: '/product-kits/',
    columns: [
      { field: 'name', header: 'Назва', editable: true, type: 'text' },
      { field: 'description', header: 'Опис', editable: true, type: 'text' },
    ],
  },
  {
    id: 'staff', label: 'Персонал', endpoint: '/staff/',
    columns: [
      { field: 'full_name', header: 'ПІБ', editable: true, type: 'text' },
      { field: 'username', header: 'Логін', editable: false },
      { field: 'role_name', header: 'Роль', editable: false },
      { field: 'is_active', header: 'Активний', editable: true, type: 'boolean' },
    ],
  },
];

function getDisplayValue(row, col) {
  const v = row[col.field];
  if (v == null || v === '') return '—';
  if (col.format) return col.format(v);
  if (col.type === 'boolean') return v ? 'Так' : 'Ні';
  return String(v);
}

function InlineCell({ value, col, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const start = () => {
    if (!col.editable) return;
    if (col.uiUnit === 'g' && value != null && value !== '') {
      setDraft(String(Math.round(Number(value) * 1000)));
    } else {
      setDraft(value == null ? '' : String(value));
    }
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    let parsed = col.type === 'number' ? parseFloat(draft) : col.type === 'boolean' ? draft === 'true' : draft;
    if (col.uiUnit === 'g' && col.type === 'number' && Number.isFinite(parsed)) {
      parsed = parsed / 1000; // г → кг для API
    }
    onSave(parsed);
  };
  const cancel = () => setEditing(false);

  if (!editing) {
    return (
      <Box
        onClick={start}
        sx={{ cursor: col.editable ? 'pointer' : 'default', minWidth: 60, '&:hover': col.editable ? { bgcolor: 'action.hover', borderRadius: 1 } : {} }}
      >
        {col.type === 'boolean'
          ? <Chip label={value ? 'Так' : 'Ні'} size="small" color={value ? 'success' : 'default'} />
          : (col.format ? col.format(value) : (value == null || value === '' ? <span style={{ color: '#bbb' }}>—</span> : String(value)))}
      </Box>
    );
  }

  if (col.type === 'select') {
    return (
      <Select size="small" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} autoFocus sx={{ minWidth: 120 }}>
        {(col.options || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </Select>
    );
  }
  if (col.type === 'boolean') {
    return (
      <Select size="small" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} autoFocus sx={{ minWidth: 80 }}>
        <MenuItem value="true">Так</MenuItem>
        <MenuItem value="false">Ні</MenuItem>
      </Select>
    );
  }
  return (
    <TextField
      size="small" autoFocus value={draft}
      type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : col.type === 'datetime-local' ? 'datetime-local' : 'text'}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 120 }}
    />
  );
}

export default function BulkEditPage() {
  const [tableId, setTableId] = useState(TABLES[0].id);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(new Set());
  const [pendingChanges, setPendingChanges] = useState({}); // id → { field: value }
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [bulkSetDialog, setBulkSetDialog] = useState(false);
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 100;

  const tableDef = TABLES.find((t) => t.id === tableId) || TABLES[0];

  const loadData = useCallback(async (tblId, pg = 1) => {
    const def = TABLES.find((t) => t.id === tblId) || TABLES[0];
    setLoading(true);
    setRows([]);
    setSelected(new Set());
    setPendingChanges({});
    try {
      const res = await api.get(`${def.endpoint}?page_size=${PAGE_SIZE}&page=${pg}`);
      const data = res.data;
      const items = Array.isArray(data) ? data : (data.results || []);
      setRows(items);
      setTotalCount(typeof data.count === 'number' ? data.count : items.length);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    setPage(1);
    setSearch('');
    loadData(tableId, 1);
  }, [tableId, loadData]);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return tableDef.columns.some((c) => String(r[c.field] ?? '').toLowerCase().includes(q));
  });

  const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      filtered.forEach((r) => next.delete(r.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filtered.forEach((r) => next.add(r.id));
      setSelected(next);
    }
  };
  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleCellSave = async (rowId, field, value) => {
    setSaving((s) => new Set(s).add(`${rowId}-${field}`));
    try {
      await api.patch(`${tableDef.endpoint}${rowId}/`, { [field]: value });
      setRows((prev) => prev.map((r) => r.id === rowId ? { ...r, [field]: value } : r));
    } catch (e) {
      alert('Помилка збереження: ' + (e?.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setSaving((s) => { const n = new Set(s); n.delete(`${rowId}-${field}`); return n; });
    }
  };

  const handleBulkDelete = async () => {
    setDeleteConfirm(false);
    const ids = Array.from(selected);
    let errors = 0;
    for (const id of ids) {
      try { await api.delete(`${tableDef.endpoint}${id}/`); }
      catch { errors++; }
    }
    if (errors > 0) alert(`Видалено з помилками: ${errors} рядків не вдалося видалити`);
    setSelected(new Set());
    loadData(tableId, page);
  };

  const handleBulkSet = async () => {
    if (!bulkField || bulkValue === '') return;
    setBulkSaving(true);
    const col = tableDef.columns.find((c) => c.field === bulkField);
    let parsedValue = bulkValue;
    if (col?.type === 'number') parsedValue = parseFloat(bulkValue);
    if (col?.type === 'boolean') parsedValue = bulkValue === 'true';
    if (col?.uiUnit === 'g' && col?.type === 'number' && Number.isFinite(parsedValue)) {
      parsedValue = parsedValue / 1000; // г → кг
    }
    let errors = 0;
    for (const id of Array.from(selected)) {
      try { await api.patch(`${tableDef.endpoint}${id}/`, { [bulkField]: parsedValue }); }
      catch { errors++; }
    }
    setBulkSaving(false);
    setBulkSetDialog(false);
    setBulkField('');
    setBulkValue('');
    if (errors > 0) alert(`${errors} рядків не вдалося оновити`);
    loadData(tableId, page);
  };

  const editableColumns = tableDef.columns.filter((c) => c.editable);
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left sidebar — table selector */}
      <Box sx={{ width: 220, flexShrink: 0, borderRight: 1, borderColor: 'divider', overflow: 'auto', bgcolor: 'background.paper' }}>
        <Typography variant="subtitle2" sx={{ px: 2, pt: 2, pb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}>
          Таблиці
        </Typography>
        {TABLES.map((t) => (
          <Box
            key={t.id}
            onClick={() => setTableId(t.id)}
            sx={{
              px: 2, py: 1, cursor: 'pointer', fontSize: 14,
              bgcolor: tableId === t.id ? 'primary.main' : 'transparent',
              color: tableId === t.id ? 'primary.contrastText' : 'text.primary',
              '&:hover': { bgcolor: tableId === t.id ? 'primary.dark' : 'action.hover' },
            }}
          >
            {t.label}
          </Box>
        ))}
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" fontWeight={700}>{tableDef.label}</Typography>
          <TextField
            size="small" placeholder="Пошук..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 220 }}
          />
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {selected.size > 0 && (
              <>
                <Chip label={`Обрано: ${selected.size}`} color="primary" />
                <Button
                  size="small" variant="outlined" color="secondary"
                  onClick={() => { setBulkField(editableColumns[0]?.field || ''); setBulkValue(''); setBulkSetDialog(true); }}
                >
                  Встановити значення
                </Button>
                <Button
                  size="small" variant="outlined" color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteConfirm(true)}
                >
                  Видалити ({selected.size})
                </Button>
              </>
            )}
            <Button size="small" variant="outlined" onClick={() => loadData(tableId, page)}>Оновити</Button>
          </Box>
        </Box>

        {/* Hint */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          Клікніть на клітинку щоб редагувати. Enter — зберегти, Escape — скасувати.
        </Typography>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" pt={6}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} sx={{ height: '100%' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selected.size > 0 && !allSelected}
                        onChange={toggleAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 50 }}>ID</TableCell>
                    {tableDef.columns.map((c) => (
                      <TableCell key={c.field} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {c.header}
                        {c.editable && <EditIcon sx={{ fontSize: 12, ml: 0.5, color: 'text.disabled', verticalAlign: 'middle' }} />}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableDef.columns.length + 2} align="center">
                        <Typography color="textSecondary" sx={{ py: 3 }}>Немає даних</Typography>
                      </TableCell>
                    </TableRow>
                  ) : filtered.map((row) => (
                    <TableRow key={row.id} selected={selected.has(row.id)} hover>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selected.has(row.id)} onChange={() => toggleRow(row.id)} />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{row.id}</TableCell>
                      {tableDef.columns.map((col) => (
                        <TableCell key={col.field} sx={{ maxWidth: 300, position: 'relative' }}>
                          {saving.has(`${row.id}-${col.field}`)
                            ? <CircularProgress size={16} />
                            : col.editable
                              ? <InlineCell
                                  value={row[col.field]}
                                  col={col}
                                  onSave={(v) => handleCellSave(row.id, col.field, v)}
                                />
                              : <span>{getDisplayValue(row, col)}</span>
                          }
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 1 }}>
            <Button size="small" disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); loadData(tableId, p); }}>← Попередня</Button>
            <Typography variant="body2">Сторінка {page} з {totalPages} ({totalCount} рядків)</Typography>
            <Button size="small" disabled={page >= totalPages} onClick={() => { const p = page + 1; setPage(p); loadData(tableId, p); }}>Наступна →</Button>
          </Box>
        )}
        {!loading && totalCount > 0 && totalPages <= 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'right' }}>
            Всього: {totalCount} рядків
          </Typography>
        )}
      </Box>

      {/* Delete confirm dialog */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Підтвердити видалення</DialogTitle>
        <DialogContent>
          <Typography>Видалити {selected.size} обраних рядків? Цю дію не можна скасувати.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>Скасувати</Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete}>Видалити</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk set dialog */}
      <Dialog open={bulkSetDialog} onClose={() => setBulkSetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Встановити значення для {selected.size} рядків</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Поле</InputLabel>
            <Select label="Поле" value={bulkField} onChange={(e) => { setBulkField(e.target.value); setBulkValue(''); }}>
              {editableColumns.map((c) => (
                <MenuItem key={c.field} value={c.field}>{c.header}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {bulkField && (() => {
            const col = tableDef.columns.find((c) => c.field === bulkField);
            if (!col) return null;
            if (col.type === 'select') return (
              <FormControl fullWidth>
                <InputLabel>Значення</InputLabel>
                <Select label="Значення" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                  {(col.options || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            );
            if (col.type === 'boolean') return (
              <FormControl fullWidth>
                <InputLabel>Значення</InputLabel>
                <Select label="Значення" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                  <MenuItem value="true">Так</MenuItem>
                  <MenuItem value="false">Ні</MenuItem>
                </Select>
              </FormControl>
            );
            return (
              <TextField
                fullWidth label="Значення"
                type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'}
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkSetDialog(false)}>Скасувати</Button>
          <Button
            variant="contained"
            disabled={bulkSaving || !bulkField || bulkValue === ''}
            onClick={handleBulkSet}
          >
            {bulkSaving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Застосувати до {selected.size} рядків
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
